import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';
import Loader from '../components/Loader';

interface Message {
  id: string;
  senderId: number;
  text: string;
  createdAt: string;
}

interface ChatInfo {
  id: string;
  otherUser: {
    telegramId: number;
    firstName: string;
    lastName?: string;
  };
}

export default function ChatDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [chat, setChat] = useState<ChatInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadChat();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChat = async () => {
    if (!id) return;
    try {
      const response = await apiService.getChat(id);
      setChat(response.data.data);
      await loadMessages();
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!id) return;
    try {
      const response = await apiService.getChatMessages(id);
      setMessages(response.data.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!id || !user || !newMessage.trim() || isSending) return;

    setIsSending(true);
    const text = newMessage.trim();
    setNewMessage('');

    // Optimistic update
    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      senderId: user.telegramId,
      text,
      createdAt: new Date().toISOString()
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      await apiService.sendMessage(id, { text });
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
      await loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
      setNewMessage(text);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return <Loader className="py-12" />;
  }

  if (!chat) {
    return (
      <div className="text-center py-8">
        <p className="text-tg-theme-hint">Чат не найден</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] -mx-4 -mt-4">
      {/* Header */}
      <div className="bg-tg-theme-bg px-4 py-3 border-b border-tg-theme-hint/20 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-tg-theme-button flex items-center justify-center text-white text-lg">
          {chat.otherUser.firstName[0]}
        </div>
        <div>
          <h2 className="font-semibold">
            {chat.otherUser.firstName} {chat.otherUser.lastName}
          </h2>
          <p className="text-xs text-tg-theme-hint">онлайн</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-tg-theme-hint">Начните общение!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === user?.telegramId;
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isOwn
                      ? 'bg-tg-theme-button text-white rounded-br-sm'
                      : 'bg-tg-theme-secondary-bg rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                  <p
                    className={`text-xs mt-1 ${
                      isOwn ? 'text-white/70' : 'text-tg-theme-hint'
                    }`}
                  >
                    {formatTime(message.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-tg-theme-bg px-4 py-3 border-t border-tg-theme-hint/20">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Сообщение..."
            className="input flex-1"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || isSending}
            className="btn-primary px-4 disabled:opacity-50"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
